import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { Link } from 'react-router-dom';

import { formatDateTime } from '@/domains/survey';
import type { IIdentifierCheck } from '../../api/identifier-check-contracts';
import { TableFrame } from '../../components/table-frame';
import { checkNumberText, checkReasonLabel } from '../../number-checks';
import { numberCheckPath } from '../../paths';
import { CheckStatusChip } from '../number-checks/number-checks-page';

/**
 * A member's number checks (SR-487 ruling 16), under its numbers in Identity & numbers — every
 * status, newest first as the card sends them: the number, why registration could not
 * corroborate it, where it stands and when it was raised, each opening its pane in Number checks,
 * where a check is decided. Nothing is decided here. Drawn only where the card carries a check:
 * an API before SR-487 sends none, and a member none was raised for has nothing to say.
 */
export function MemberChecks({
  participantId,
  checks,
  headingLevel
}: {
  participantId: string;
  checks: IIdentifierCheck[] | undefined;
  /** 3 under the section's own heading; 4 under a member's, in a household of more than one. */
  headingLevel: 3 | 4;
}) {
  if (!checks || checks.length === 0) return null;
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  return (
    <div className="grid gap-3">
      <Heading className="text-sm font-medium text-[var(--color-royal-blue)]">Number checks</Heading>
      <TableFrame>
        <Table aria-label="Number checks">
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Raised</TableHead>
              <TableHead>
                <span className="sr-only">Open</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {checks.map((check) => (
              <TableRow key={check.checkId} data-check={check.checkId}>
                <TableCell className="align-top">{checkNumberText(check)}</TableCell>
                <TableCell className="align-top">{checkReasonLabel(check.reason)}</TableCell>
                <TableCell className="align-top">
                  <CheckStatusChip status={check.status} />
                </TableCell>
                <TableCell className="align-top whitespace-nowrap">
                  {formatDateTime(check.createdAt)}
                </TableCell>
                <TableCell className="align-top whitespace-nowrap">
                  <Link
                    to={numberCheckPath(participantId, check.checkId)}
                    className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
                  >
                    Open the check
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableFrame>
    </div>
  );
}
